/**
 * Words for parked driver data (OPEN-144, DEC-357): kinds, decisions, refusals, and the
 * timeline events. One place, so the review panel, the job modal and the Sync page agree.
 */

/**
 * The API's SYNC_CONFLICT_REASON_MIN (@lodex-pro/shared sync-conflicts.ts). Mirrored, not
 * imported: that module has no browser-safe subpath export, and the barrel may only be
 * imported for types here. A test pins the two equal.
 */
export const REASON_MIN = 10;

/**
 * Offline shift start (migration 0190) — items about a SHIFT, not a job: what the server had to
 * decide when a START SHIFT or truck change made offline arrived. Informational; Dismiss only.
 * The shared SHIFT_CONFLICT_KINDS, mirrored for the same reason as REASON_MIN.
 */
const SHIFT_KIND_LABELS: Readonly<Record<string, string>> = {
  SHIFT_MERGED: 'Shift joined',
  SHIFT_OVERLAP: 'Shift overlap',
  TRUCK_IN_USE: 'Truck in use',
  EQUIPMENT_INVALID: 'Retired truck',
  EQUIPMENT_MISSING: 'No truck',
};

const KIND_LABELS: Readonly<Record<string, string>> = {
  DUPLICATE_CAPTURE: 'Second run ticket',
  LATE_FIELD_DATA: 'Late field data',
  COMPLETION_VS_CANCELLATION: 'Completed after cancel',
  BUSINESS_OVERRIDE: 'Office override',
  STALE_WRITE: 'Out-of-date change',
  // #60 (DEC-365) — a driver's pickup-site change the server could not apply.
  SITE_CHANGE: 'Site change',
  // §58 PR 4 — a driver's load for a customer this company cannot take new work for. Named
  // by what it is, never by the plan (Q14).
  EXPRESS_CUSTOMER: 'Other customer',
  // #914 M3 — a disposal the driver picked that the server could not use (retired, unknown, or
  // more than one). The ticket reaches review with no actual disposal; this keeps what was sent.
  INVALID_DROPOFF_TARGET: 'Unusable disposal',
  // #920 — a driver's disposal that arrived after the office recorded "no disposal happened".
  NO_DISPOSAL_OVERRIDE: 'Disposal after none',
  // OPEN-157 B — sent by a driver after the office took the job back from them.
  TAKEN_BACK: 'After take back',
};

/** The kinds the Sync page filter offers, in this order. */
export const KIND_FILTERS: ReadonlyArray<{ value: string; label: string }> = Object.entries({
  ...KIND_LABELS,
  ...SHIFT_KIND_LABELS,
}).map(([value, label]) => ({ value, label }));

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? SHIFT_KIND_LABELS[kind] ?? kind.replaceAll('_', ' ').toLowerCase();
}

const RESOLUTION_LABELS: Readonly<Record<string, string>> = {
  KEPT_SAVED: 'Kept the saved ticket',
  APPLIED_PARKED: 'Used the parked ticket',
  SUPERSEDED: 'Another parked ticket was used',
  DISMISSED: 'Dismissed',
};

export function resolutionLabel(resolution: string | null, kind?: string): string {
  if (resolution === null) return 'Open';
  if (resolution === 'APPLIED_PARKED' && kind === 'SITE_CHANGE') {
    return 'Applied the driver’s site change';
  }
  return RESOLUTION_LABELS[resolution] ?? resolution;
}

/** What a refused decision means (the API's codes; any 400 from /apply is "checks"). */
export function decisionErrorMessage(
  action: 'keep' | 'apply' | 'dismiss' | 'apply-site-change',
  status: number | null,
  code: string | null,
): string {
  switch (code) {
    case 'PARKED_APPLY_NOT_IN_REVIEW':
      return 'Only a ticket In Review can be swapped.';
    case 'PARKED_APPLY_VOLUME_EDITED':
      return 'Revert the volume edit on this ticket first, then use the parked ticket.';
    case 'PARKED_APPLY_SAVED_CHANGED':
      return 'The saved ticket changed after this one was parked. Keep it, or Send Back.';
    case 'SYNC_CONFLICT_NOT_OPEN':
      return 'Someone already decided this one. The page has been reloaded.';
    case 'SYNC_CONFLICT_KIND_MISMATCH':
      return 'That action doesn’t apply to this item.';
    // OPEN-157 B — a taken-back run ticket, once the job already has one.
    case 'TAKEN_BACK_APPLY_CAPTURED':
      return 'This job already has a run ticket, so this one can’t be used. Dismiss it, or fix the saved ticket in review.';
    case 'JOB_TRANSITION_FORBIDDEN':
      return 'This job is closed or billed, so it takes no run ticket. Dismiss this one.';
    // #60 (DEC-365) — applying a parked site change, refused by the same rules as Change site.
    case 'SITE_CHANGE_LOCKED':
      return 'This ticket is approved, so its site can’t change. Dismiss this, or send the ticket back to review first.';
    case 'SITE_CUSTOMER_MISMATCH':
      return 'That site belongs to another customer, so it can’t be applied. Dismiss this one.';
    case 'PERMISSION_DENIED':
      return 'You don’t have permission to do that.';
    default:
      if (action === 'apply' && status === 400) {
        return 'The parked ticket no longer passes the ticket checks (a tank or product changed since). Keep the saved ticket, or Send Back.';
      }
      if (action === 'apply-site-change' && status === 400) {
        return 'That site can no longer be used (it was retired, or the well is no longer on the location). Dismiss this one, or change the site from the job.';
      }
      return 'Couldn’t save that. Try again.';
  }
}

/** The four parked-ticket audit events, in words, for the job and review timelines. */
export const PARKED_AUDIT_LABELS: Readonly<Record<string, string>> = {
  CAPTURE_DUPLICATE_PARKED: 'Second run ticket parked',
  CAPTURE_PARKED_KEPT: 'Kept the saved ticket',
  CAPTURE_PARKED_APPLIED: 'Used the parked ticket',
  SYNC_CONFLICT_DISMISSED: 'Parked item dismissed',
};

export function parkedAuditLabel(eventType: string): string | null {
  return PARKED_AUDIT_LABELS[eventType] ?? null;
}
